import { useEffect, useState } from "react";
import { CloudOff, Download, RefreshCw, X } from "lucide-react";
import { usePWA } from "@/lib/pwa";

/**
 * The three things the app ever needs to say about itself: you are offline, a
 * new version is ready, and this can be installed.
 *
 * They share one fixed stack anchored above the tab bar so they can never
 * overlap each other or cover it, and so a phone with a home indicator gets the
 * same clearance the rest of the app gets.
 */
export function PWAPrompts() {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 px-3"
      style={{
        bottom: "calc(var(--bottom-nav-height, 0px) + 0.75rem)",
      }}
    >
      <OfflineNotice />
      <UpdateNotice />
      <InstallNotice />
    </div>
  );
}

/* ── Offline ────────────────────────────────────────────────────────────────
   Announced, not just drawn. Someone using a screen reader has no other way to
   learn that the reason nothing is loading is the connection.
   ─────────────────────────────────────────────────────────────────────────── */

function OfflineNotice() {
  const { online } = usePWA();

  /*
    A flap between offline and online — a lift doorway, a tunnel — should not
    flash a banner. The bar waits a beat before appearing, and leaves at once
    when the connection returns.
  */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (online) {
      setVisible(false);
      return;
    }
    const id = window.setTimeout(() => setVisible(true), 1200);
    return () => window.clearTimeout(id);
  }, [online]);

  return (
    <div role="status" aria-live="polite" className="contents">
      {visible && (
        <div className="pointer-events-auto flex w-full max-w-md items-center gap-2.5 rounded-xl border border-border bg-secondary px-3.5 py-2.5 text-secondary-foreground shadow-[var(--shadow-modal)]">
          <CloudOff className="h-4 w-4 shrink-0" aria-hidden />
          <p className="text-sm font-semibold">
            You're offline.{" "}
            <span className="font-normal opacity-80">Showing your last saved results.</span>
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Update ─────────────────────────────────────────────────────────────── */

function UpdateNotice() {
  const { updateReady, applyUpdate, dismissUpdate } = usePWA();
  if (!updateReady) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 shadow-[var(--shadow-modal)]"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <p className="flex-1 text-sm font-semibold text-ink">A new version of CavaYo is ready.</p>
      <button
        type="button"
        onClick={applyUpdate}
        className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground transition hover:opacity-90"
      >
        Reload
      </button>
      <button
        type="button"
        onClick={dismissUpdate}
        aria-label="Dismiss update notice"
        className="shrink-0 rounded-lg p-1.5 text-ink-muted transition hover:bg-muted hover:text-ink"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

/* ── Install ────────────────────────────────────────────────────────────── */

const INSTALL_DISMISSED_KEY = "CavaYo:install-dismissed-at";
const ASK_AGAIN_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

function dismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < ASK_AGAIN_AFTER_MS;
  } catch {
    // Private mode, or storage disabled. Treat as never dismissed.
    return false;
  }
}

function InstallNotice() {
  const { canInstall, promptInstall, isStandalone } = usePWA();
  const [dismissed, setDismissed] = useState(true);

  /*
    Held back until the visitor has been here a moment. An install card that
    appears on top of a page still painting is asking someone to commit to an
    app they have not seen, and it is the single most reliably dismissed thing
    a web app can show.
  */
  useEffect(() => {
    if (!canInstall || isStandalone || dismissedRecently()) return;
    const id = window.setTimeout(() => setDismissed(false), 25_000);
    return () => window.clearTimeout(id);
  }, [canInstall, isStandalone]);

  const close = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
    } catch {
      /* nothing to remember it with — it will ask again next visit */
    }
  };

  if (dismissed || !canInstall || isStandalone) return null;

  return (
    <div className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 shadow-[var(--shadow-modal)]">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
        <Download className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-ink">Install CavaYo</p>
        <p className="truncate text-xs text-ink-muted">Faster, full screen, works offline.</p>
      </div>
      <button
        type="button"
        onClick={async () => {
          const accepted = await promptInstall();
          if (!accepted) close();
        }}
        className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground transition hover:opacity-90"
      >
        Install
      </button>
      <button
        type="button"
        onClick={close}
        aria-label="Not now"
        className="shrink-0 rounded-lg p-1.5 text-ink-muted transition hover:bg-muted hover:text-ink"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
